import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { inflate } from 'zlib';

import { Horizon } from '../models';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { 
  allDieFields, 
  allHorizonFields, 
  allHorizonStatFields 
} from '../../ngrx/graphql';
import { SET_MAIN_VIEW } from '../../ngrx/actions';
import { Observable } from 'rxjs';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class HorizonService {
  constructor(
    private authService: AuthenticationService,
    private credentialService: CredentialService,
    private store: Store<any>
  ) {}

  private invokeHorizons(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.batchFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            } 
            observer.error(err.stack);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      )
    });
  }

  private invokeHorizonsMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.batchFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
        }
      }
    );
  }

  getAll(): Observable<any> {
    const query = `{
      dies(param: "all") {
        ${allDieFields}
      }

      horizons(param: "list") {
        ${allHorizonFields}
      }
    }`;

    /**
     * horizonStats(param: "all") {
        ${allHorizonStatFields}
      }
     */

    return this.invokeHorizons({ query });
  }

  getAllHorizons(): Observable<any> {
    const query = `{
      horizons(param: "list") {
        ${allHorizonFields}
      }
    }`;

    /**
     * horizonStats(param: "all") {
        ${allHorizonStatFields}
      }
     */

    return this.invokeHorizons({ query });

  }
  getAllActiveHorizons(): Observable<any> {
    const query = `{
      horizons(param: "all") {
        ${allHorizonFields}
      }
    }`;

    return this.invokeHorizons({ query });
  }

  getDie(id: string): Observable<any> {
    const query = `{
      die(id: "${id}") {
        ${allDieFields}
      }
    }`;

    return this.invokeHorizons({ query });
  }

  getHorizon(id: string): Observable<any> {
    const query = `{
      horizon(id: "${id}") {
        ${allHorizonFields}
      }
    }`;

    return this.invokeHorizons({ query });
  }

  getHorizonList(): Observable<Horizon[]> {
    return this.store.select('horizons', 'horizonList');
  }

  getHorizonStat(id: string): Observable<any> {
    const query = `{
      horizonStat(id: "${id}") {
        ${allHorizonStatFields}
      }
    }`;

    return this.invokeHorizons({ query });
  }

  getHorizonConfig(): any {
    const horizonConfig = localStorage.getItem('horizonConfig');

    if (!horizonConfig || !Object.keys(horizonConfig).length) {
      localStorage.setItem('horizonConfig', JSON.stringify({}));
      return {};
    } else {
      return JSON.parse(horizonConfig);
    }
  }

  setHorizonConfig(horizon: any = {}): void {
    localStorage.setItem('horizonConfig', JSON.stringify(horizon));
    this.store.dispatch({
      type: SET_MAIN_VIEW,
      payload: { horizon }
    });
  }

  clearHorizonConfig(): void {
    try {
      if (typeof Storage !== 'undefined' && localStorage) {
        localStorage.removeItem('horizonConfig');
      }
    } catch (error) {
      console.warn('Failed to clear horizon config from localStorage:', error);
    }
  }

  updateDie(die: any): void {
    const variables = { die };
    const query = `{

    }`;

    this.invokeHorizonsMutation({ query, variables });
  }
  // const query = `mutation($job: JobInput!) {
  //   updateJob(job: $job) {
  //     ${allJobFields}
  //   }    updateHorizon(
      // horizon: HorizonInput!
      // )
  // }`;
  updateHorizon(horizon: any): void {
    const variables = { horizon };
    const query = `mutation ($horizon: HorizonInput!) {
      updateHorizon(horizon: $horizon) {
        ${allHorizonFields}
      }
    }`;

    this.invokeHorizonsMutation({ query, variables });
  }

  updateHorizonCapacity(horizon: any): void {
    const variables = { horizon };
    const query = `mutation ($horizon: HorizonInput!) {
      updateHorizonCapacity(horizon: $horizon) {
        ${allHorizonFields}
      }
    }`;

    this.invokeHorizonsMutation({ query, variables });
  }
}


// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/services/horizon.service.ts